/**
 * EditFormV1 — Generic reusable edit form component
 *
 * Works for any module (jobs, candidates, submissions, etc.)
 * All module-specific behaviour is passed in via props — zero hardcoding inside.
 *
 * Props:
 *   moduleName      string    — e.g. "job", "candidate"
 *   recordId        string    — MongoDB _id of the record
 *   breadcrumbItems array     — passed to AppBreadcrumb
 *   submitText      string    — Save button label (default "Save Changes")
 *   cancelPath      string    — navigate here on cancel/success
 *   onSuccess       function  — called after successful save (overrides cancelPath)
 *   fieldNameMap    object    — rename form fields before sending to API
 *                              e.g. { mspReqId: 'mspreqId' }
 *   onFileUpload    function  — (fieldName, file, recordId) => Promise
 *                              handle multipart uploads per module
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Form,
    Input,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    TimePicker,
    Tooltip,
    Typography,
    Upload,
    message,
} from 'antd';
import {
    CalendarOutlined,
    CloudUploadOutlined,
    DeleteOutlined,
    DownOutlined,
    EnvironmentOutlined,
    EyeOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FileWordOutlined,
    InfoCircleOutlined,
    LockOutlined,
    MailOutlined,
    PhoneOutlined,
    PlusOutlined,
    SolutionOutlined,
    TagsOutlined,
    UpOutlined,
    UserOutlined,
} from '@ant-design/icons';
import '../styles/AddFormV1.css';
import AppBreadcrumb from './Breadcrumb';
import TipTapEditor from './TipTapEditor';
import DocumentViewer from './DocumentViewer';
import { applyInputValidator, getKeyPattern } from './form/inputValidator';
import { configuredEmailRule } from './form/emailValidator';
import { securityValidationRule } from './form/inputSecurity';
import { uploadAcceptCategory } from './form/uploadAccept';
import { roleAllowsAction } from '../utils/roleAllowsAction';
import { groupAllowedByRole, fieldAllowedByRole } from '../utils/roleFormPermissionGate';
import { resolveAfterSubmit, safeNavTarget } from '../utils/afterSubmitNav';
import { useNavigate } from 'react-router-dom';
import { fetchJsonWithAuth } from '../services/authApi';
import { AUTH_URL } from '../services/apiConfig';
import { buildFileUrl } from './detail/renderConfig';
import { getDropdownValues } from '../services/moduleDataApi';
import { getFormGroups } from '../services/addFormApi';
import { updateRecord } from '../services/detailedViewApi';
import { getRoleFormPermissions } from '../services/adminApi';
import {
    buildPayload as buildConfigPayload,
    buildRepeatFieldInitial,
    singleFieldOf,
    collectFileParts,
    effectiveDataType,
    getDeep,
    toSelectControlValue,
    usesLabeledSelectValue,
} from './form/payloadTransformer';
import { computeLinkedSets, findLinkedSet, padLinkedInitialValues } from './form/linkedAddRowGroups';
import { getEditFormFallbackOptions } from './form/editFormOptionFallbacks';
import useAiActions, { groupActionsByPosition } from './form/useAiActions';
import AiActionButtonGroup from './form/AiActionButtons';
import { namePathFromString, buildAddRowInitial, applyScalarFieldValues } from './form/applyGroupValues';

const { Text } = Typography;
const { TextArea } = Input;


const sectionIcons = [
    <UserOutlined />,
    <EnvironmentOutlined />,
    <SolutionOutlined />,
    <InfoCircleOutlined />,
    <FileTextOutlined />,
];

const fieldIcons = {
    email: <MailOutlined className="v1-input-icon" />,
    contactNumber: <PhoneOutlined className="v1-input-icon" />,
    phone: <PhoneOutlined className="v1-input-icon" />,
    currentLocation: <EnvironmentOutlined className="v1-input-icon" />,
    preferredLocation: <EnvironmentOutlined className="v1-input-icon" />,
    designation: <SolutionOutlined className="v1-input-icon" />,
    skills: <TagsOutlined className="v1-input-icon" />,
    firstName: <UserOutlined className="v1-input-icon" />,
    lastName: <UserOutlined className="v1-input-icon" />,
    jobTitle: <SolutionOutlined className="v1-input-icon" />,
    jobLocation: <EnvironmentOutlined className="v1-input-icon" />,
    vendorEmail: <MailOutlined className="v1-input-icon" />,
};

// ─── Pure helpers ────────────────────────────────────────────────────────────

const truthy = (v) => v === true || v === 1 || v === '1';
const editable = (field, moduleName) => (field?.edit === undefined || truthy(field.edit))
    && (!field?.editablePermission || roleAllowsAction(field.editablePermission, moduleName));
const empty = (v) => v === undefined || v === null || v === '';

function hasMeaningfulValue(value) {
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0 && value.some(hasMeaningfulValue);
    if (typeof value === 'object') return Object.values(value).some(hasMeaningfulValue);
    return true;
}

function normalizeModule(name = '') {
    const n = String(name).toLowerCase();
    if (n === 'job') return 'jobs';
    if (n === 'candidate') return 'candidates';
    if (n === 'submissions') return 'submission';
    return n;
}

function normalizeOptions(options = []) {
    return options.map((o) => {
        if (typeof o === 'string') return { label: o, value: o };
        return {
            label: o.label ?? o.name ?? o.value ?? o.userName ?? o.email,
            value: o.value ?? o.id ?? o.userId ?? o._id ?? o.name ?? o.label,
        };
    });
}

function getNestedProp(obj, dotPath) {
    return String(dotPath ?? '').split('.').reduce((cur, k) => cur?.[k], obj);
}

function FieldLabel({ field, label, locked }) {
    if (!label) return label;
    const showInfo = truthy(field?.infoEnabled) && !empty(field?.infoText);
    if (!showInfo && !locked) return label;
    return (
        <span className="v1-field-label-with-info">
            <span>{label}</span>
            {showInfo && (
                <Tooltip title={field.infoText}>
                    <InfoCircleOutlined className="v1-field-info-icon" />
                </Tooltip>
            )}
            {locked && (
                <Tooltip title="You don't have permission to edit this field">
                    <LockOutlined className="v1-field-info-icon" />
                </Tooltip>
            )}
        </span>
    );
}

function GroupInfoMessage({ group }) {
    if (!truthy(group?.infoEnabled) || empty(group?.infoText)) return null;
    return <Text type="secondary" className="v1-group-info-message">{group.infoText}</Text>;
}

function GroupToggleControl({ group }) {
    if (!truthy(group?.toggleEnabled) || empty(group?.toggleField)) return null;
    return (
        <Space size={6} align="center" onClick={(event) => event.stopPropagation()}>
            <Text type="secondary" className="v1-combine-toggle-label">{group.toggleLabel || 'Toggle'}</Text>
            <Form.Item name={group.toggleField} valuePropName="checked" initialValue={Boolean(group.toggleValue)} noStyle>
                <Switch size="small" />
            </Form.Item>
        </Space>
    );
}

function normalizeGroups(groups = [], moduleName, rolePerms) {
    return [...groups]
        .filter((g) => !truthy(g.hideGroup) && groupAllowedByRole(rolePerms, g.name))
        .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
        .map((g) => ({
            ...g,
            fields: [...(g.fields ?? [])]
                // A field with a blank key can't render or submit — drop it so
                // one stray admin-config row can't break the whole group. Also
                // drop any field this role's Form Configuration (Role Configure
                // → Form, derived from this same Form Groups config) turned off.
                .filter((f) => String(f.field ?? '').trim() !== ''
                    && (f.show === undefined || truthy(f.show))
                    && (!f.visiblePermission || roleAllowsAction(f.visiblePermission, moduleName))
                    && fieldAllowedByRole(rolePerms, g.name, f.field))
                .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)),
        }))
        .filter((g) => g.fields.length);
}

function stripHtml(html) {
    return String(html ?? '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim();
}

function getValidationValue(field, type) {
    const rules = field.validations ?? field.validation ?? field.rules ?? [];
    const rule = rules.find((r) => (typeof r === 'string' ? r : r?.type) === type);
    if (!rule || typeof rule === 'string') return undefined;
    return rule.value ?? rule.length ?? rule[type];
}

const numberRangeRule = ({ label, message: msg, value, compare, text }) => ({
    validator: (_, input) => {
        if (empty(input)) return Promise.resolve();
        return compare(Number(input), Number(value))
            ? Promise.resolve()
            : Promise.reject(new Error(msg ?? `${label} ${text} ${value}`));
    },
});

function getSiblingFieldName(currentName, compareField) {
    const compareParts = String(compareField).split('.').filter(Boolean);
    if (!Array.isArray(currentName)) return compareParts.length > 1 ? compareParts : compareField;
    if (compareParts.length > 1) {
        // A DB rule may use `endDate` or `work_experience.endDate`. Preserve
        // the current Form.List row and avoid repeating the group segment.
        if (currentName.length >= 3 && typeof currentName[1] === 'number') {
            const listPrefix = currentName.slice(0, 2);
            const relativeParts = compareParts[0] === String(currentName[0])
                ? compareParts.slice(1)
                : compareParts;
            return [...listPrefix, ...relativeParts];
        }
        return compareParts;
    }
    const nextName = [...currentName];
    nextName[nextName.length - 1] = compareParts[0];
    return nextName;
}

function getComparableDateTime(value) {
    if (!value) return null;
    if (typeof value.valueOf === 'function') {
        const time = value.valueOf();
        return Number.isNaN(time) ? null : time;
    }
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
}

function getComparableDateDay(value) {
    if (!value) return null;
    const date = dayjs(value);
    return date.isValid() ? date.startOf('day').valueOf() : null;
}

// Time-of-day (TimePicker) values share a day, so comparing via valueOf()/startOf('day')
// makes every time equal — compare minutes-since-midnight instead.
function getComparableTimeOfDay(value) {
    if (!value) return null;
    const time = dayjs(value);
    return time.isValid() ? time.hour() * 60 + time.minute() : null;
}

const crossFieldNumberRule = ({ label, message, value, rule, form, name, compare, text }) => ({
    validator: async (_, input) => {
        const compareField = value ?? rule.compareField ?? rule.field;
        if (!compareField || empty(input)) return Promise.resolve();
        const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
        if (empty(compareValue)) return Promise.resolve();
        const inputNumber = Number(input);
        const compareNumber = Number(compareValue);
        if (Number.isNaN(inputNumber) || Number.isNaN(compareNumber)) return Promise.resolve();
        return compare(inputNumber, compareNumber)
            ? Promise.resolve()
            : Promise.reject(new Error(message ?? `${label} ${text} ${compareField}`));
    },
});

const validationRulesByType = {
    noPastDate: ({ label, message: msg }) => ({
        validator: (_, value) => {
            if (empty(value)) return Promise.resolve();
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return getComparableDateTime(value) < today.getTime()
                ? Promise.reject(new Error(msg ?? `${label} cannot be a past date`))
                : Promise.resolve();
        },
    }),
    noFutureDate: ({ label, message: msg }) => ({
        validator: (_, value) => {
            if (empty(value)) return Promise.resolve();
            const endOfDay = new Date();
            endOfDay.setHours(23, 59, 59, 999);
            return getComparableDateTime(value) > endOfDay.getTime()
                ? Promise.reject(new Error(msg ?? `${label} cannot be a future date`))
                : Promise.resolve();
        },
    }),
    minAge: ({ message: msg, value }) => ({
        validator: (_, input) => {
            if (empty(input)) return Promise.resolve();
            const years = Number(value ?? 18);
            const cutoff = new Date();
            cutoff.setFullYear(cutoff.getFullYear() - years);
            cutoff.setHours(23, 59, 59, 999);
            return getComparableDateTime(input) > cutoff.getTime()
                ? Promise.reject(new Error(msg ?? `Must be at least ${years} years old`))
                : Promise.resolve();
        },
    }),
    dateAfterField: ({ label, message: msg, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField || empty(input)) return Promise.resolve();
            const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
            if (empty(compareValue)) return Promise.resolve();
            const inputTime = getComparableDateDay(input);
            const compareTime = getComparableDateDay(compareValue);
            if (inputTime === null || compareTime === null || inputTime >= compareTime) return Promise.resolve();
            return Promise.reject(new Error(msg ?? `${label} must be on or after ${compareField}`));
        },
    }),
    dateBeforeField: ({ label, message: msg, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField || empty(input)) return Promise.resolve();
            const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
            if (empty(compareValue)) return Promise.resolve();
            const inputTime = getComparableDateDay(input);
            const compareTime = getComparableDateDay(compareValue);
            if (inputTime === null || compareTime === null || inputTime <= compareTime) return Promise.resolve();
            return Promise.reject(new Error(msg ?? `${label} must be on or before ${compareField}`));
        },
    }),
    timeAfterField: ({ label, message: msg, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField || empty(input)) return Promise.resolve();
            const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
            if (empty(compareValue)) return Promise.resolve();
            const inputTime = getComparableTimeOfDay(input);
            const compareTime = getComparableTimeOfDay(compareValue);
            if (inputTime === null || compareTime === null || inputTime > compareTime) return Promise.resolve();
            return Promise.reject(new Error(msg ?? `${label} must be after ${compareField}`));
        },
    }),
    timeBeforeField: ({ label, message: msg, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField || empty(input)) return Promise.resolve();
            const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
            if (empty(compareValue)) return Promise.resolve();
            const inputTime = getComparableTimeOfDay(input);
            const compareTime = getComparableTimeOfDay(compareValue);
            if (inputTime === null || compareTime === null || inputTime < compareTime) return Promise.resolve();
            return Promise.reject(new Error(msg ?? `${label} must be before ${compareField}`));
        },
    }),
    greaterThanField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input >= other, text: 'must be greater than or equal to' }),
    strictGreaterThanField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input > other, text: 'must be greater than' }),
    strictLessThanField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input < other, text: 'must be less than' }),
    lessThanOrEqualField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input <= other, text: 'must be less than or equal to' }),
    maxFieldWithFallback: ({ label, message: msg, value, rule, form, name }) => ({
        validator: async (_, input) => {
            if (empty(input)) return Promise.resolve();
            const fieldNames = String(value ?? rule.compareFields ?? '').split(',').map((item) => item.trim()).filter(Boolean);
            const selectedField = fieldNames.find((fieldName) => !empty(form.getFieldValue(getSiblingFieldName(name, fieldName))));
            if (!selectedField) return Promise.resolve();
            const maximum = Number(form.getFieldValue(getSiblingFieldName(name, selectedField)));
            const inputNumber = Number(input);
            if (Number.isNaN(maximum) || Number.isNaN(inputNumber) || inputNumber <= maximum) return Promise.resolve();
            return Promise.reject(new Error(msg ?? `${label} must not exceed ${selectedField}`));
        },
    }),
    required: ({ field, message: msg, form, name }) => ({
        validator: (_, value) => {
            // A field disabled via disabledByField (e.g. endDate while
            // currentlyPursuing is checked) must not block submit with a
            // "required" error the user has no way to satisfy.
            if (field?.disabledByField && form && name) {
                const disabledNow = truthy(form.getFieldValue(getSiblingFieldPath(name, field.disabledByField)));
                if (disabledNow) return Promise.resolve();
            }
            let isEmpty =
                value === undefined || value === null || value === '' ||
                (Array.isArray(value) && value.length === 0);
            if (!isEmpty && field?.type === 'text-editor') isEmpty = stripHtml(value) === '';
            return isEmpty
                ? Promise.reject(new Error(msg ?? 'Mandatory Field'))
                : Promise.resolve();
        },
    }),
    email: ({ label, message: msg }) => configuredEmailRule(label, msg),
    url: ({ label, message: msg }) => ({ type: 'url', message: msg ?? `Enter valid ${label}` }),
    number: ({ label, message: msg }) => ({ pattern: /^[0-9]+$/, message: msg ?? `${label} must be a number` }),
    phone: ({ message: msg }) => ({ pattern: /^[0-9]{10}$/, message: msg ?? 'Phone number must contain exactly 10 digits' }),
    len: ({ label, message: msg, value }) => ({ len: Number(value), message: msg ?? `${label} must be ${value} characters` }),
    pattern: ({ label, message: msg, rule }) => ({
        pattern: new RegExp(rule.pattern ?? rule.value, rule.flags),
        message: msg ?? `Enter valid ${label}`,
    }),
    min: ({ field, label, message: msg, value }) =>
        field.type === 'number'
            ? numberRangeRule({ label, message: msg, value, compare: (i, m) => i >= m, text: 'must be at least' })
            : {
                validator: (_, input) => {
                    if (input == null || input === '') return Promise.resolve();
                    const content = field.type === 'text-editor' ? stripHtml(input) : String(input);
                    if (content === '') return Promise.resolve();
                    return content.length >= Number(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(msg ?? `${label} must be at least ${value} characters`));
                },
            },
    max: ({ field, label, message: msg, value }) =>
        field.type === 'number'
            ? numberRangeRule({ label, message: msg, value, compare: (i, m) => i <= m, text: 'must not exceed' })
            : {
                validator: (_, input) => {
                    if (input == null || input === '') return Promise.resolve();
                    const content = field.type === 'text-editor' ? stripHtml(input) : String(input);
                    if (content === '') return Promise.resolve();
                    return content.length <= Number(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(msg ?? `${label} must not exceed ${value} characters`));
                },
            },
};

function validationToRule(validation, field, form, name) {
    const rule = typeof validation === 'string' ? { type: validation } : validation;
    const type = rule.type ?? rule.rule ?? rule.name;
    const value = rule.value ?? rule.length ?? rule.min ?? rule.max;
    const label = field.label ?? field.field ?? 'Field';
    const factory = validationRulesByType[type];
    return factory ? factory({ field, label, message: rule.message, rule, value, form, name }) : rule;
}

function getSiblingFieldPath(name, siblingField) {
    return Array.isArray(name) && name.length > 1
        ? [...name.slice(0, -1), siblingField]
        : [siblingField];
}

// A field is required either via the simple "req" toggle or via a "required"
// entry in the admin-configured validations list — the Form.Item asterisk and
// the addRow "at least one row" check must honour both, not just field.req.
function isFieldRequired(field) {
    if (truthy(field?.req)) return true;
    const apiRules = field?.validations ?? field?.validation ?? field?.rules ?? [];
    return apiRules.some((rule) => (typeof rule === 'string' ? rule : rule?.type) === 'required');
}

function getRules(field, form, name, skipRequired = false) {
    const label = field.label ?? field.field ?? 'Field';
    const apiRules = field.validations ?? field.validation ?? field.rules ?? [];
    const hasApiRequired = apiRules.some((r) => (typeof r === 'string' ? r : r?.type) === 'required');
    const hasApiEmail = apiRules.some((r) => (typeof r === 'string' ? r : r?.type) === 'email');
    const rules = [];
    if (!skipRequired && truthy(field.req) && !hasApiRequired) rules.push(validationRulesByType.required({ field, message: 'Mandatory Field', form, name }));
    if ((field.type === 'email' || field.formatter === 'email') && !hasApiEmail) {
        rules.push(configuredEmailRule(label));
    }
    // Number fields are whole-number-only unless the admin marks them decimal
    // (e.g. a commission of 5.5). Both patterns still reject any non-numeric
    // character, so "float only" stays enforced either way.
    if (field.type === 'number') {
        rules.push(truthy(field.decimal)
            ? { pattern: /^\d+(\.\d+)?$/, message: `${label} must be a number` }
            : { pattern: /^[0-9]+$/, message: `${label} must be a number` });
    }
    return [...rules, ...apiRules.map((r) => validationToRule(r, field, form, name)), securityValidationRule(field)];
}

function getFieldDependencies(field, currentName) {
    const supportedTypes = new Set([
        'greaterThanField', 'strictGreaterThanField', 'strictLessThanField',
        'lessThanOrEqualField', 'maxFieldWithFallback', 'dateAfterField', 'dateBeforeField',
        'timeAfterField', 'timeBeforeField',
    ]);
    return (field.validations ?? field.validation ?? field.rules ?? [])
        .filter((rule) => supportedTypes.has(typeof rule === 'string' ? rule : rule?.type))
        .flatMap((rule) => String(rule.value ?? rule.compareField ?? rule.field ?? '')
            .split(',').map((item) => item.trim()).filter(Boolean)
            .map((compareField) => getSiblingFieldName(currentName, compareField)));
}

function getInputLimits(field) {
    const len = field.len ?? getValidationValue(field, 'len');
    const min = field.min ?? getValidationValue(field, 'min');
    const max = field.max ?? getValidationValue(field, 'max');
    if (field.type === 'number') return { min: min ?? undefined, max: max ?? undefined };
    return { minLength: min ?? undefined, maxLength: field.maxLength ?? len ?? max ?? undefined };
}

function getDisabledDate(field, form, name) {
    const rules = field.validations ?? field.validation ?? field.rules ?? [];
    const predicates = [];
    for (const rule of rules) {
        const type = typeof rule === 'string' ? rule : rule?.type;
        if (type === 'noPastDate') {
            predicates.push((current) => {
                if (!current) return false;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return getComparableDateTime(current) < today.getTime();
            });
        }
        if (type === 'noFutureDate') {
            predicates.push((current) => {
                if (!current) return false;
                const endOfDay = new Date();
                endOfDay.setHours(23, 59, 59, 999);
                return getComparableDateTime(current) > endOfDay.getTime();
            });
        }
        if (type === 'minAge') {
            const years = Number(rule?.value ?? 18);
            predicates.push((current) => {
                if (!current) return false;
                const cutoff = new Date();
                cutoff.setFullYear(cutoff.getFullYear() - years);
                cutoff.setHours(23, 59, 59, 999);
                return getComparableDateTime(current) > cutoff.getTime();
            });
        }
        if (type === 'dateAfterField' || type === 'dateBeforeField') {
            const compareField = rule?.value ?? rule?.compareField ?? rule?.field;
            if (!compareField) continue;
            predicates.push((current) => {
                if (!current) return false;
                const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
                if (empty(compareValue)) return false;
                const currentTime = getComparableDateDay(current);
                const compareTime = getComparableDateDay(compareValue);
                if (currentTime === null || compareTime === null) return false;
                return type === 'dateAfterField' ? currentTime < compareTime : currentTime > compareTime;
            });
        }
    }
    return predicates.length ? (current) => predicates.some((predicate) => predicate(current)) : undefined;
}

function getDefaultPickerValue(field) {
    const rules = field.validations ?? field.validation ?? field.rules ?? [];
    for (const rule of rules) {
        const type = typeof rule === 'string' ? rule : rule?.type;
        if (type === 'minAge') return dayjs().subtract(Number(rule?.value ?? 18), 'year');
        if (type === 'noFutureDate' || type === 'noPastDate') return dayjs();
    }
    return undefined;
}

function cleanValue(value, formatter) {
    if (!formatter) return value;
    if (formatter === 'phone' || formatter === 'digits') return value.replace(/\D/g, '').slice(0, 10);
    if (formatter === 'email' || formatter === 'noSpaces') return value.replace(/\s/g, '');
    if (formatter === 'name') return value.replace(/\s+/g, ' ').trimStart().replace(/\b\w/g, (c) => c.toUpperCase());
    return value;
}

// ─── FieldControl ─────────────────────────────────────────────────────────────

function FieldControl({ field, form, name, value, checked, fileList, moduleName, initialValue, scopeValues = {}, onAiUpload, formDisabled }) {
    const type = field.type ?? 'text';
    const baseDisabled = !editable(field, moduleName);

    // ALL hooks at the top — before any conditional returns
    const [dynamicOptions, setDynamicOptions] = useState(null);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [locationSearch, setLocationSearch] = useState('');
    const [lookupSearch, setLookupSearch] = useState('');
    const [masterSearch, setMasterSearch] = useState('');
    const [viewer, setViewer] = useState({ open: false, docs: [], index: 0 });
    const [inputError, setInputError] = useState(null);
    const rawLookupRef = useRef([]);
    const filterValuePath = field.lookupFilterValueFrom ? namePathFromString(field.lookupFilterValueFrom) : [`__lookup_filter_${field.field}`];
    const formFilterValue = Form.useWatch(filterValuePath, form);
    // scopeValues (e.g. AddFormV1's client-gate id) only wins when it actually
    // carries a value — a key present but undefined/empty (e.g. EditFormV1's
    // `{ clientId }` when no clientId prop was passed, as on the Jobs edit
    // page) must fall back to the field's own live form value, or the lookup
    // filter is permanently starved and the dropdown never resolves a name.
    const scopeFilterValue = field.lookupFilterValueFrom
        ? scopeValues[field.lookupFilterValueFrom]
        : undefined;
    const lookupFilterValue = !empty(scopeFilterValue) ? scopeFilterValue : formFilterValue;
    const disabledBySiblingPath = field.disabledByField
        ? (Array.isArray(name) && name.length > 1 ? [...name.slice(0, -1), field.disabledByField] : [field.disabledByField])
        : ['__noop_disabled_watch__'];
    const disabledByValue = Form.useWatch(disabledBySiblingPath, form);
    const disabled = baseDisabled || (field.disabledByField ? truthy(disabledByValue) : false) || Boolean(formDisabled);

    useEffect(() => {
        if (type === 'location') {
            let cancelled = false;

            async function fetchLocations() {
                try {
                    setOptionsLoading(true);
                    const params = new URLSearchParams({
                        search: locationSearch,
                        limit: String(field.limit ?? 50),
                        offset: '0',
                    });
                    const json = await fetchJsonWithAuth(AUTH_URL, `/admin/location-dropdown-values?${params}`);
                    const raw = json?.data ?? json ?? [];
                    if (!cancelled) setDynamicOptions(normalizeOptions(Array.isArray(raw) ? raw : []));
                } catch (err) {
                    console.error(`[EditFormV1] Failed to fetch locations for "${field.field}":`, err);
                    if (!cancelled) setDynamicOptions([]);
                } finally {
                    if (!cancelled) setOptionsLoading(false);
                }
            }

            fetchLocations();
            return () => { cancelled = true; };
        }
        const ds = field.dataSource;
        if (!['select', 'radio', 'checkbox'].includes(type)) return undefined;
        if (!ds || ds === 'static') return undefined;
        if (ds === 'lookup' && field.lookupFilterField && empty(lookupFilterValue)) {
            setDynamicOptions([]);
            setOptionsLoading(false);
            return undefined;
        }

        let cancelled = false;

        async function fetchOptions() {
            try {
                setOptionsLoading(true);
                let raw = [];

                if (ds === 'master' || ds === 'module') {
                    raw = await getDropdownValues(
                        moduleName,
                        field.field,
                        ds === 'master' ? masterSearch : '',
                        field.limit ?? 50,
                        0,
                        {
                            dataSource: ds,
                            masterName: field.masterName,
                            groupName: field.groupName,
                        },
                    );
                } else if (ds === 'lookup') {
                    const params = new URLSearchParams({
                        collection: field.lookupCollection ?? '',
                        displayField: field.displayField ?? '',
                        valueField: field.valueField ?? '_id',
                    });
                    if (field.displayField2) params.set('displayField2', field.displayField2);
                    if (lookupSearch) params.set('search', lookupSearch);
                    if (field.lookupFilterField && !empty(lookupFilterValue)) {
                        params.set('filterField', field.lookupFilterField);
                        params.set('filterValue', String(lookupFilterValue));
                    }
                    if (field.extraFields?.length) params.set('extraFields', field.extraFields.join(','));
                    const json = await fetchJsonWithAuth(AUTH_URL, `/admin/lookup-dropdown-values?${params}`);
                    raw = json?.data ?? json ?? [];
                } else if (ds === 'group') {
                    const groupName = String(field.groupName ?? '').toLowerCase().trim();
                    if (groupName === 'users' || groupName === 'user') {
                        const json = await fetchJsonWithAuth(AUTH_URL, '/get-all-users?limit=500&offset=0');
                        const users = json?.data?.users ?? json?.users ?? json?.data ?? [];
                        raw = Array.isArray(users)
                            ? users.map((u) => ({
                                label: [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
                                    || u.userName || u.email || String(u.userId ?? u.id),
                                value: u.userId ?? u.id,
                            }))
                            : [];
                    }
                }

                if (!cancelled) {
                    rawLookupRef.current = Array.isArray(raw) ? raw : [];
                    setDynamicOptions(normalizeOptions(rawLookupRef.current));
                }
            } catch (err) {
                console.error(`[EditFormV1] Failed to fetch options for "${field.field}":`, err);
                if (!cancelled) setDynamicOptions([]);
            } finally {
                if (!cancelled) setOptionsLoading(false);
            }
        }

        fetchOptions();
        return () => { cancelled = true; };
    }, [type, field.dataSource, field.field, field.lookupCollection,
        field.displayField, field.displayField2, field.valueField, field.lookupFilterField, lookupFilterValue, field.groupName, field.masterName, field.limit, locationSearch, lookupSearch, masterSearch, moduleName, field.extraFields]);

    // ── Derived values ──
    const configuredOptions = [field.options, field.values]
        .find((items) => Array.isArray(items) && items.length > 0)
        ?? getEditFormFallbackOptions(field);
    const staticOptions = normalizeOptions(configuredOptions);
    const options = dynamicOptions ?? staticOptions;

    const selectMode = field.multiSelect
        ? 'multiple'
        : field.mode ?? (truthy(field.multiple) ? 'multiple' : undefined);
    const inferredSelectMode = selectMode ?? (effectiveDataType(field) === 'array' ? 'multiple' : undefined);

    const isSelectLike = ['select', 'location'].includes(type);
    const placeholder = field.hidePlaceholder
        ? undefined
        : (field.placeholder ?? `${isSelectLike ? 'Select' : 'Enter'} ${String(field.label ?? '').toLowerCase()}`);
    const inputLimits = getInputLimits(field);

    const validateField = () => {
        setTimeout(() => { form.validateFields([name]).catch(() => { }); });
    };

    const updateAndValidate = (nextValue) => {
        form.setFieldValue(name, nextValue);
        validateField();
    };

    // Build validator config once (null when not set)
    const validatorCfg = field.validator?.type
        ? { ...field.validator, title: field.validator.title || field.label || 'Field' }
        : null;

    const inputProps = {
        ...inputLimits,
        value,
        disabled,
        placeholder,
        prefix: field.icon ? null : fieldIcons[field.field],
        onChange: (e) => {
            let next = field.formatter
                ? cleanValue(e.target.value, field.formatter)
                : e.target.value;

            if (validatorCfg) {
                const { cleaned, error } = applyInputValidator(next, validatorCfg, 'change');
                setInputError(error);
                next = cleaned;
            }

            updateAndValidate(next);
        },
        ...(validatorCfg && {
            onBlur: (e) => {
                const { cleaned, error } = applyInputValidator(e.target.value, validatorCfg, 'blur');
                setInputError(error);
                if (cleaned !== e.target.value) updateAndValidate(cleaned);
            },
            onKeyPress: (e) => {
                const pattern = getKeyPattern(validatorCfg.type);
                if (!pattern) return;
                if (e.which === 0 || e.keyCode === 8 || e.keyCode === 13) return;
                const char = String.fromCharCode(e.which ?? e.keyCode);
                if (!pattern.test(char)) {
                    e.preventDefault();
                    const hint = `${validatorCfg.title} — invalid character.`;
                    setInputError(hint);
                    setTimeout(() => setInputError((prev) => (prev === hint ? null : prev)), 1800);
                }
            },
            maxLength: validatorCfg.maxLength ?? inputLimits.maxLength,
        }),
    };

    // ── Renders ──

    if (type === 'tagify') {
        return (
            <Select value={value} allowClear disabled={disabled} mode="tags"
                placeholder={placeholder} onChange={updateAndValidate} />
        );
    }

    if (type === 'location') {
        return (
            <Select
                value={value}
                allowClear
                showSearch
                filterOption={false}
                disabled={disabled}
                loading={optionsLoading}
                options={options}
                placeholder={placeholder}
                notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
                onSearch={setLocationSearch}
                onFocus={() => setLocationSearch((current) => current)}
                onChange={updateAndValidate}
            />
        );
    }

    if (type === 'select') {
        const remoteLookup = field.dataSource === 'lookup';
        const remoteMaster = field.dataSource === 'master';
        const remoteSearch = remoteLookup || remoteMaster;
        const localGroupSearch = field.dataSource === 'group';
        const keepLabel = usesLabeledSelectValue(field);
        const controlValue = toSelectControlValue(field, value, options);
        const handleSelectChange = (val) => {
            updateAndValidate(val);
            if (field.autofillFrom?.length) {
                const selectedValue = val && !Array.isArray(val) && typeof val === 'object' ? val.value : val;
                const isInsideList = Array.isArray(name) && name.length >= 3 && typeof name[1] === 'number';
                if (val == null) {
                    field.autofillFrom.forEach(({ targetField: tf }) => {
                        const tp = isInsideList
                            ? [...name.slice(0, 2), ...namePathFromString(tf)]
                            : namePathFromString(tf);
                        form.setFieldValue(tp, null);
                    });
                } else if (rawLookupRef.current.length > 0) {
                    const vField = field.valueField ?? '_id';
                    const rawItem = rawLookupRef.current.find(item =>
                        getNestedProp(item, vField) === selectedValue || item._id === selectedValue || item.id === selectedValue || item.value === selectedValue
                    );
                    if (rawItem) {
                        field.autofillFrom.forEach(({ optionKey, targetField: tf }) => {
                            const v = getNestedProp(rawItem, optionKey) ?? rawItem.extraData?.[optionKey] ?? null;
                            const tp = isInsideList
                                ? [...name.slice(0, 2), ...namePathFromString(tf)]
                                : namePathFromString(tf);
                            form.setFieldValue(tp, v);
                        });
                    }
                }
            }
        };
        return (
            <Select value={controlValue} allowClear disabled={disabled} loading={optionsLoading}
                labelInValue={keepLabel || undefined}
                showSearch={remoteSearch || localGroupSearch || undefined}
                optionFilterProp="label"
                filterOption={remoteSearch ? false : undefined}
                mode={inferredSelectMode} options={options} placeholder={placeholder}
                notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
                onSearch={remoteLookup ? setLookupSearch : remoteMaster ? setMasterSearch : undefined}
                onChange={handleSelectChange} />
        );
    }

    if (type === 'radio') {
        return (
            <Radio.Group value={value} disabled={disabled} options={options}
                onChange={(e) => updateAndValidate(e.target.value)} />
        );
    }

    if (type === 'checkbox') {
        if (options.length > 0) {
            return (
                <Checkbox.Group value={value} disabled={disabled} options={options}
                    onChange={updateAndValidate} />
            );
        }
        return (
            <Checkbox
                checked={checked}
                disabled={disabled}
                onChange={(e) => {
                    const next = e.target.checked;
                    updateAndValidate(next);
                    if (field.linkedClearField && next) {
                        const clearPath = Array.isArray(name) && name.length > 1
                            ? [...name.slice(0, -1), field.linkedClearField]
                            : [field.linkedClearField];
                        form.setFieldValue(clearPath, null);
                    }
                }}
            >
                {field.checkboxLabel ?? field.label}
            </Checkbox>
        );
    }

    if (type === 'text-editor') {
        return (
            <>
                <TipTapEditor
                    value={value ?? initialValue ?? ''}
                    disabled={disabled}
                    placeholder={placeholder}
                    onChange={(html) => {
                        if (validatorCfg) {
                            const { error } = applyInputValidator(html, validatorCfg, 'change');
                            setInputError(error);
                        }
                        updateAndValidate(html);
                    }}
                />
                {inputError && <div className="v1-input-validator-hint">{inputError}</div>}
            </>
        );
    }

    if (type === 'textarea') {
        return (
            <>
                <TextArea {...inputProps} rows={field.rows ?? 3} className="v1-scroll-textarea" />
                {inputError && <div className="v1-input-validator-hint">{inputError}</div>}
            </>
        );
    }

    if (type === 'date') {
        return (
            <DatePicker value={value} disabled={disabled} placeholder={placeholder}
                style={{ width: '100%' }} suffixIcon={<CalendarOutlined />}
                disabledDate={getDisabledDate(field, form, name)}
                defaultPickerValue={getDefaultPickerValue(field)}
                onChange={(date) => updateAndValidate(date)} />
        );
    }

    if (type === 'time') {
        return (
            <TimePicker value={value} disabled={disabled} placeholder={placeholder}
                style={{ width: '100%' }} onChange={(time) => updateAndValidate(time)} />
        );
    }

    if (type === 'file') {
        const fileRules = field.validations ?? field.validation ?? field.rules ?? [];
        const fileTypeRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'fileType');
        const fileSizeRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'fileSize');
        const maxFilesRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'maxFiles');

        // Admin "Allowed Content" category (field.accept) takes priority over the
        // fine-grained fileType rule; 'any'/unset falls through to the rule.
        const acceptCategory = uploadAcceptCategory(field);
        const acceptTypes = acceptCategory
            ? acceptCategory.exts.map((e) => `.${e}`).join(',')
            : (fileTypeRule?.value
                ? fileTypeRule.value.split(',').map((e) => `.${e.trim().replace(/^\./, '')}`).join(',')
                : undefined);
        const maxSizeMB = fileSizeRule ? Number(fileSizeRule?.value ?? 5) : undefined;
        // maxFiles validation rule takes top priority; otherwise use field.multiple/maxCount
        // (set via admin panel toggle), falling back to single-file mode.
        const maxFiles = maxFilesRule
            ? Number(maxFilesRule?.value ?? 1)
            : (truthy(field.multiple) ? (field.maxCount ?? 5) : 1);

        const fileList = Array.isArray(value) ? value : [];
        const canAddMore = fileList.length < maxFiles;

        const formatFileSize = (bytes) => {
            if (!bytes) return '';
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
            return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
        };

        const getFileIcon = (fileName) => {
            const ext = (fileName ?? '').split('.').pop()?.toLowerCase();
            if (ext === 'pdf') return <FilePdfOutlined className="v1-file-type-icon v1-file-icon-pdf" />;
            if (['doc', 'docx'].includes(ext)) return <FileWordOutlined className="v1-file-type-icon v1-file-icon-word" />;
            if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileExcelOutlined className="v1-file-type-icon v1-file-icon-excel" />;
            if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return <FileImageOutlined className="v1-file-type-icon v1-file-icon-image" />;
            return <FileOutlined className="v1-file-type-icon v1-file-icon-default" />;
        };

        const handleFileDelete = (index) => {
            updateAndValidate(fileList.filter((_, i) => i !== index));
        };

        const handleFileView = (file, fileIdx) => {
            const docs = fileList.map((f) => {
                let url = f.url ?? f.thumbUrl ?? '';
                if (!url && f.originFileObj instanceof File) url = URL.createObjectURL(f.originFileObj);
                if (url && !url.startsWith('http') && !url.startsWith('blob:')) url = buildFileUrl(url);
                return { url, name: f.name ?? 'file' };
            }).filter(d => d.url);
            setViewer({ open: true, docs, index: fileIdx ?? 0 });
        };

        const isMulti = maxFiles > 1;
        const remaining = maxFiles - fileList.length;

        const typeHint = acceptCategory
            ? acceptCategory.hint
            : (acceptTypes
                ? acceptTypes.split(',').map((t) => t.replace('.', '').toUpperCase()).join(', ')
                : null);
        const sizeHint = maxSizeMB ? `up to ${maxSizeMB} MB` : null;
        const baseHint = [typeHint, sizeHint].filter(Boolean).join(' ');
        const dropHint = isMulti && fileList.length > 0
            ? [baseHint, `${remaining} of ${maxFiles} remaining`].filter(Boolean).join(' • ')
            : baseHint;
        const dropTitle = fileList.length > 0
            ? `Add ${isMulti ? 'more files' : 'file'}`
            : (field.buttonLabel ?? `Upload ${field.label ?? 'File'}`);

        const beforeUploadHandler = (file) => {
            if (fileList.length >= maxFiles) return Upload.LIST_IGNORE;
            if (acceptTypes) {
                const ext = (file.name.split('.').pop() ?? '').toLowerCase();
                const allowed = acceptTypes.split(',').map((t) => t.replace(/^\./, '').toLowerCase());
                if (!allowed.includes(ext)) {
                    message.error(acceptCategory
                        ? acceptCategory.error
                        : (fileTypeRule?.message ?? `Only ${fileTypeRule.value} files are allowed`));
                    return Upload.LIST_IGNORE;
                }
            }
            if (maxSizeMB !== undefined && file.size / 1024 / 1024 > maxSizeMB) {
                message.error(fileSizeRule?.message ?? `File must be smaller than ${maxSizeMB} MB`);
                return Upload.LIST_IGNORE;
            }
            return false;
        };

        // antd calls onChange once per file even in multi-select, each time with the
        // full accumulated newList. Slicing to maxFiles guards against racing additions.
        const handleUploadChange = ({ fileList: newList }) => {
            const sliced = newList.slice(0, maxFiles);
            updateAndValidate(sliced);
            // An "upload"-trigger AI action (e.g. auto-parse a resume) fires once
            // per newly-added file, using the raw browser File object.
            if (onAiUpload && sliced.length > fileList.length) {
                const added = sliced[sliced.length - 1];
                if (added?.originFileObj) onAiUpload(added.originFileObj);
            }
        };

        const multiUploadProps = {
            accept: acceptTypes,
            fileList,
            showUploadList: false,
            multiple: true,
            maxCount: maxFiles,
            beforeUpload: beforeUploadHandler,
            onChange: handleUploadChange,
            disabled,
        };

        const fileRows = fileList.map((file, index) => (
            <div key={file.uid ?? index} className="v1-file-item">
                <div className="v1-file-item-icon">{getFileIcon(file.name)}</div>
                <div className="v1-file-item-info">
                    <span className="v1-file-item-name">{file.name}</span>
                    <span className="v1-file-item-meta">
                        {file.existing
                            ? 'Previously uploaded'
                            : `${formatFileSize(file.size)}${file.size ? ' • ' : ''}Uploaded just now`}
                    </span>
                </div>
                <Space size={6} className="v1-file-item-actions">
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleFileView(file, index)}
                        className="v1-file-view-btn"
                    >
                        View
                    </Button>
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleFileDelete(index)}
                        className="v1-file-delete-btn"
                    />
                </Space>
            </div>
        ));

        const viewerEl = (
            <DocumentViewer
                documents={viewer.docs}
                open={viewer.open}
                onClose={() => setViewer(v => ({ ...v, open: false }))}
                initialIndex={viewer.index}
            />
        );

        if (isMulti) {
            const multiTitle = fileList.length > 0
                ? `Upload more ${field.label ?? 'files'}`
                : (field.buttonLabel ?? `Upload ${field.label ?? 'File'}`);

            return (
                <>
                    <div className="v1-file-upload-wrapper">
                        {/* Centered dropzone — always at top in multiple mode */}
                        {canAddMore && (
                            <Upload {...multiUploadProps} style={{ width: '100%', display: 'block' }}>
                                <div className={`v1-upload-dropzone v1-upload-dropzone-center${disabled ? ' v1-upload-dropzone-disabled' : ''}`}>
                                    <div className="v1-upload-dropzone-title-row">
                                        <CloudUploadOutlined className="v1-upload-cloud-icon v1-upload-cloud-icon-sm" />
                                        <span className="v1-upload-dropzone-title v1-upload-dropzone-title-blue">{multiTitle}</span>
                                    </div>
                                    <span className="v1-upload-dropzone-sub">Drag & drop or click to upload</span>
                                    {baseHint && <span className="v1-upload-dropzone-sub">{baseHint}</span>}
                                </div>
                            </Upload>
                        )}

                        {/* Uploaded file rows — 2-column grid */}
                        {fileList.length > 0 && (
                            <div className="v1-file-grid">
                                {fileRows}
                            </div>
                        )}

                        {/* "+ Add more files" text link at the bottom */}
                        {canAddMore && fileList.length > 0 && (
                            <Upload {...multiUploadProps} style={{ display: 'inline-block' }}>
                                <Button type="link" size="small" icon={<PlusOutlined />} className="v1-add-more-files-btn">
                                    Add more files
                                </Button>
                            </Upload>
                        )}
                    </div>
                    {viewerEl}
                </>
            );
        }

        return (
            <>
                <div className="v1-file-upload-wrapper">
                    {/* Single mode: compact file row at field height */}
                    {fileList.length > 0 && <div className="v1-file-rows-single">{fileRows}</div>}

                    {canAddMore && (
                        <Upload
                            accept={acceptTypes}
                            fileList={fileList}
                            showUploadList={false}
                            multiple={false}
                            maxCount={1}
                            beforeUpload={beforeUploadHandler}
                            onChange={handleUploadChange}
                            disabled={disabled}
                            style={{ width: '100%', display: 'block' }}
                        >
                            <div className={`v1-upload-dropzone v1-upload-dropzone-single${disabled ? ' v1-upload-dropzone-disabled' : ''}`}>
                                <CloudUploadOutlined className="v1-upload-cloud-icon" />
                                <span className="v1-upload-dropzone-title">{dropTitle}</span>
                                {dropHint && <span className="v1-upload-dropzone-sub">— {dropHint}</span>}
                            </div>
                        </Upload>
                    )}
                </div>
                {viewerEl}
            </>
        );
    }

    return (
        <>
            <Input
                {...inputProps}
                type={type === 'number' ? 'number' : type}
                // step="any" so the browser's own number input accepts a
                // fractional value on a field the admin marked decimal.
                {...(type === 'number' && truthy(field.decimal) ? { step: 'any' } : {})}
                // Admin-configured unit shown inside the input (e.g. "%").
                {...(field.suffix ? { suffix: field.suffix } : {})}
            />
            {inputError && <div className="v1-input-validator-hint">{inputError}</div>}
        </>
    );
}

// ─── SectionTitle ─────────────────────────────────────────────────────────────

function SectionTitle({ icon, title }) {
    return (
        <Space size={10} align="center">
            <div className="v1-section-icon-box">{icon}</div>
            <Text strong className="v1-section-label">{title}</Text>
        </Space>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function EditFormV1({
    moduleName,
    recordId,
    clientId,
    region,
    breadcrumbItems = [],
    submitText = 'Save Changes',
    cancelPath,
    onSuccess,
    // fieldNameMap: rename keys before sending to API
    // e.g. { mspReqId: 'mspreqId' }
    // Once backend standardises field names this prop can be removed
    fieldNameMap = {},
    // onFileUpload: (fieldName, file, recordId) => Promise
    // Handle multipart file uploads per module outside this component
    onFileUpload = null,
}) {
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [collapsed, setCollapsed] = useState({});
    const [formGroups, setFormGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    // Role Configure → Form permissions for the CURRENT user's role, derived
    // server-side from this module's Form Groups config. null until loaded /
    // when unavailable, in which case normalizeGroups' rolePerms checks fail
    // open — no behavior change for a role/module that hasn't been configured.
    const [rolePerms, setRolePerms] = useState(null);

    useEffect(() => {
        let cancelled = false;
        queueMicrotask(() => {
            if (cancelled) return;
            if (!moduleName) { setRolePerms(null); return; }
            // Same client/region scope the form's own structure is fetched
            // with (getFormGroups above) — a client-scoped Form Groups
            // override must gate the SAME scoped structure, not the
            // tenant-wide default.
            getRoleFormPermissions({ roleId: localStorage.getItem('roleId') }, moduleName, { clientId, region })
                .then((data) => { if (!cancelled) setRolePerms(Array.isArray(data) && data.length ? data : null); })
                .catch(() => { if (!cancelled) setRolePerms(null); });
        });
        return () => { cancelled = true; };
    }, [moduleName, clientId, region]);

    const groups = useMemo(() => normalizeGroups(formGroups, moduleName, rolePerms), [formGroups, moduleName, rolePerms]);
    // group.linkGroup — addRow groups sharing the same value stay row-synced
    // (see form/linkedAddRowGroups.js). Empty for every group with no
    // linkGroup, i.e. every group that predates this feature.
    const linkedGroupSets = useMemo(() => computeLinkedSets(groups), [groups]);
    // Registry of each addRow group's live Form.List {add, remove}, written on
    // every render from inside the Form.List's own render-prop (synchronous
    // within the same commit, so it's fully populated before a user can click
    // anything). A linked group's "Add Row" / row-delete calls every member's
    // add()/remove() through this registry; unlinked groups just call their own.
    const listControlsRef = useRef({});
    const aiActions = useAiActions({ form, groups, module: moduleName });

    // Per-addRow-group Form.List initialValue (one row object per backend-embedded
    // stored row). Computed from the same groups the form renders, so it is ready
    // the moment each Form.List mounts. Linked groups are then padded to equal
    // length (see padLinkedInitialValues) — a no-op for every unlinked group.
    const addRowInitials = useMemo(() => {
        const out = {};
        groups.forEach((group) => {
            if (group.addRow) out[group.name] = buildAddRowInitial(group);
        });
        return padLinkedInitialValues(linkedGroupSets, out);
    }, [groups, linkedGroupSets]);

    const MODULE_LABELS = {
        jobs: { list: 'Jobs', path: '/jobs', edit: 'Edit Job' },
        candidates: { list: 'Candidates', path: '/candidates', edit: 'Edit Candidate' },
        submissions: { list: 'Submissions', path: '/submissions', edit: 'Edit Submission' },
    };

    const resolvedBreadcrumbs = breadcrumbItems.length > 0
        ? breadcrumbItems
        : (() => {
            const config = MODULE_LABELS[normalizeModule(moduleName)];
            if (!config) return [];
            return [
                { label: 'Home', href: '/' },
                { label: config.list, href: config.path },
                { label: config.edit },
            ];
        })();


    // ── Fetch form config + record ──────────────────────────────────────────

    useEffect(() => {
        if (!moduleName || !recordId) return undefined;

        let cancelled = false;

        async function fetchData() {
            try {
                setLoading(true);
                setError('');
                setSubmitError('');

                // Single source of truth: the form-groups config with `id` embeds
                // each field's current value (field.value). No separate detail-view
                // fetch — prefill reads field.value only.
                const groupsResponse = await getFormGroups({ module: moduleName, id: recordId, clientId, region });

                if (cancelled) return;

                setFormGroups(Array.isArray(groupsResponse) ? groupsResponse : []);
            } catch (err) {
                console.error(`Failed to fetch ${moduleName} edit data:`, err);
                if (!cancelled) setError(err?.message || 'Failed to load record');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchData();
        return () => { cancelled = true; };
    }, [moduleName, recordId, clientId, region]);

    // ── Prefill form ────────────────────────────────────────────────────────
    //
    // Single source of truth: each field's `field.value`, embedded by getFormGroups
    // when an `id` is passed. Shaped and applied via the shared applyGroupValues
    // module, the same code an AI Action's response is applied through.

    useEffect(() => {
        if (loading || groups.length === 0) return;
        applyScalarFieldValues(form, groups);
    }, [loading, groups, form]);

    // ── Collapse toggle ─────────────────────────────────────────────────────

    const toggle = (key) => {
        setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    // ── Payload builder — the SAME config-driven engine AddFormV1 uses ──────────
    //
    // No field-name / module-specific logic. The engine:
    //   • starts from the untouched record (base) so unedited fields are preserved
    //   • applies each field's payloadKey / dataType / payloadMode / template
    //   • skips file fields (uploaded separately via onFileUpload)
    //
    // fieldNameMap is an optional caller-provided rename map kept for backward
    // compatibility; new renames should use the field's payloadKey config instead.
    function buildPayload(values) {
        // No base record: every editable value comes from the form (prefilled from
        // getFormGroups field.value). The payload carries only the configured fields.
        const payload = buildConfigPayload(values, groups, { module: moduleName });

        Object.entries(fieldNameMap).forEach(([from, to]) => {
            if (payload[from] !== undefined) {
                payload[to] = payload[from];
                delete payload[from];
            }
        });

        return payload;
    }

    // ── Submit ──────────────────────────────────────────────────────────────

    async function submit(values) {
        try {
            setSubmitting(true);
            setSubmitError('');

            // New files ride along in the SAME module/update multipart, exactly
            // like create — config-driven fileKey (jobs → "file", candidates →
            // resume/passport/documents/…). The downstream update handler stores
            // them and returns name/location. This replaces the old per-module
            // onFileUpload endpoints (which pointed candidate edits at the jobs
            // attachment URL). onFileUpload is still honoured as an optional
            // override when a module needs bespoke upload handling.
            // Indexed keys (documents[0], documents[1], …) so the update handler
            // can align each new file to its metadata row by position. Create uses
            // flat keys; only update needs indexing.
            const fileParts = collectFileParts(values, groups, { indexed: true, module: moduleName });

            if (onFileUpload && onFileUpload.useLegacyUpload) {
                const fileFieldDefs = groups
                    .flatMap((g) => g.fields ?? [])
                    .filter((f) => f.type === 'file');
                for (const field of fileFieldDefs) {
                    const list = values[field.field];
                    if (!Array.isArray(list)) continue;
                    const newFiles = list.filter((f) => f.originFileObj instanceof File);
                    try {
                        await Promise.all(
                            newFiles.map((f) => onFileUpload(field.field, f.originFileObj, recordId)),
                        );
                    } catch (uploadErr) {
                        console.warn('File upload failed — continuing with save:', uploadErr);
                    }
                }
            }

            // Save JSON payload + new files together through the gateway.
            await updateRecord(moduleName, recordId, buildPayload(values),
                onFileUpload?.useLegacyUpload ? [] : fileParts);

            message.success(`${moduleName} updated successfully`);

            if (onSuccess) {
                onSuccess();
            } else {
                const afterSubmit = resolveAfterSubmit(groups);
                if (!afterSubmit) {
                    // No admin-configured behavior: preserve existing default navigation.
                    if (cancelPath) navigate(cancelPath);
                    else navigate(-1);
                } else if (afterSubmit.mode === 'back') {
                    navigate(-1);
                } else if (afterSubmit.mode === 'route' || afterSubmit.mode === 'detail') {
                    const target = safeNavTarget(afterSubmit.target, recordId, values);
                    if (target) navigate(target);
                    else if (cancelPath) navigate(cancelPath);
                    else navigate(-1);
                }
                // afterSubmit.mode === 'stay': no navigation.
            }
        } catch (err) {
            console.error(`Failed to update ${moduleName}:`, err);
            setSubmitError(err?.message || 'Failed to save changes');
            message.error(err?.message || 'Failed to save changes');
        } finally {
            setSubmitting(false);
        }
    }

    // ── Scroll to first error on validation fail ────────────────────────────

    const handleSubmitFailed = ({ errorFields }) => {
        if (!errorFields?.length) return;
        const errorFormItem = document.querySelector('.ant-form-item-has-error');
        if (!errorFormItem) return;
        const input = errorFormItem.querySelector('input, textarea, select, .ProseMirror');
        if (!input) return;
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
            input.focus?.();
            input.style.boxShadow = '0 0 0 3px rgba(255, 77, 79, 0.3)';
            setTimeout(() => { input.style.boxShadow = ''; }, 1000);
        }, 300);
    };

    // ── Render ──────────────────────────────────────────────────────────────

    const [combineToggles, setCombineToggles] = useState({});
    const fieldKey = (field) => String(field?.field ?? '');

    const evaluateShowIf = (showIf, getFieldValue, siblingPrefix) => {
        if (!showIf?.field) return true;
        const path = siblingPrefix != null
            ? [...(Array.isArray(siblingPrefix) ? siblingPrefix : [siblingPrefix]), showIf.field]
            : [showIf.field];
        const val = getFieldValue(path);
        switch (showIf.operator) {
            case 'eq': return String(val ?? '') === String(showIf.value ?? '');
            case 'neq': return String(val ?? '') !== String(showIf.value ?? '');
            case 'truthy': return val !== undefined && val !== null && val !== '' && val !== false;
            case 'falsy': return val === undefined || val === null || val === '' || val === false;
            default: return true;
        }
    };

    const renderField = (field, namePrefix, listName, opts = {}) => {
        const fieldParts = typeof field.field === 'string' && field.field.includes('.')
            ? field.field.split('.')
            : null;
        const parsedField = fieldParts ?? field.field;
        const name = namePrefix !== undefined
            ? [namePrefix, ...(fieldParts ?? [field.field])]
            : parsedField;
        // listName is normally a single Form.List name (string), but a DOUBLY-nested
        // list (a repeat field's subFields rendered inside an addRow group row) passes
        // the full absolute path array of its own list — spread it instead of wrapping.
        const absoluteName = (listName !== undefined && namePrefix !== undefined)
            ? [...(Array.isArray(listName) ? listName : [listName]), ...(Array.isArray(name) ? name : [name])]
            : name;
        const isCheckboxGroup =
            field.type === 'checkbox' &&
            ((field.options && field.options.length > 0) ||
                (field.dataSource && field.dataSource !== 'static'));

        const valuePropName = !isCheckboxGroup && field.type === 'checkbox'
            ? 'checked'
            : 'value';

        // Inline (combined) fields show no per-box label — the combined card prints a
        // single label and the boxes sit side by side (e.g. From / To / Currency).
        // hideLabel: the group renders one shared header row above all addRow rows
        // instead of repeating each field's label on every row (e.g. Languages Known).
        const permissionLocked = Boolean(field.editablePermission)
            && !roleAllowsAction(field.editablePermission, moduleName);
        const fieldLabel = (opts.inline || opts.hideLabel)
            ? ''
            : !isCheckboxGroup && field.type === 'checkbox'
            ? ''
            : <FieldLabel field={field} label={field.label} locked={permissionLocked} />;

        const mdSpan = opts.inline
            ? 24
            : field.type === 'text-editor' || (field.type === 'file' && truthy(field.multiple))
            ? 24
            : (field.col ?? 12);
        const showIfPrefix = (listName !== undefined && namePrefix !== undefined)
            ? [...(Array.isArray(listName) ? listName : [listName]), namePrefix]
            : null;
        const dependencies = getFieldDependencies(field, absoluteName);

        // Inside a combined block, each box's width follows its combineWidth weight
        // (flex-grow), so the pack can be sized by ratio (e.g. 2:1, 1:2:3). Unset → 1.
        const inlineStyle = opts.inline
            ? { flex: `${Number(field.combineWidth) > 0 ? Number(field.combineWidth) : 1} 1 0`, maxWidth: 'none' }
            : undefined;

        // AI Actions: admin-configured buttons (Generate/Parse-style) attached to
        // this field. "click" actions render as buttons beside the field; an
        // "upload" action (on a file field) fires automatically when a new file
        // is accepted, via the onAiUpload callback passed into FieldControl.
        const clickAiActions = (field.aiActions || []).filter((a) => a.trigger === 'click');
        const uploadAiAction = (field.aiActions || []).find((a) => a.trigger === 'upload');

        const fieldItem = (
            <Form.Item
                label={fieldLabel}
                name={name}
                extra={field.helpText ? <Text type="secondary" className="v1-field-help-text">{field.helpText}</Text> : undefined}
                required={isFieldRequired(field)}
                rules={getRules(field, form, absoluteName)}
                dependencies={dependencies}
                valuePropName={valuePropName}
                getValueFromEvent={field.type === 'file' ? (e) => Array.isArray(e) ? e : e?.fileList : undefined}
                validateTrigger={['onBlur', 'onChange']}
            >
                <FieldControl
                    field={field}
                    form={form}
                    name={absoluteName}
                    moduleName={moduleName}
                    initialValue={field.value}
                    scopeValues={{ clientId }}
                    onAiUpload={uploadAiAction
                        ? (file) => aiActions.runAction(opts.groupNameForActions, field, uploadAiAction, file)
                        : undefined}
                    formDisabled={aiActions.busy}
                />
            </Form.Item>
        );

        const fieldItemWithActions = clickAiActions.length > 0 ? (() => {
            const positioned = groupActionsByPosition(clickAiActions);
            const group = (position) => (
                <AiActionButtonGroup
                    key={position}
                    position={position}
                    actions={positioned[position]}
                    aiActions={aiActions}
                    groupName={opts.groupNameForActions}
                    field={field}
                />
            );
            return (
                <div className="v1-ai-action-wrap">
                    {group('top-left')}
                    {group('top-center')}
                    {group('top-right')}
                    {fieldItem}
                    {group('bottom-left')}
                    {group('bottom-center')}
                    {group('bottom-right')}
                </div>
            );
        })() : fieldItem;

        if (field.showIf?.field) {
            return (
                <Form.Item key={field.field} shouldUpdate noStyle>
                    {({ getFieldValue }) =>
                        evaluateShowIf(field.showIf, getFieldValue, showIfPrefix)
                            ? <Col xs={24} md={mdSpan} style={inlineStyle} className={opts.inline ? 'v1-combine-cell' : undefined}>{fieldItemWithActions}</Col>
                            : null
                    }
                </Form.Item>
            );
        }

        return (
            <Col key={field.field} xs={24} md={mdSpan} style={inlineStyle} className={opts.inline ? 'v1-combine-cell' : undefined}>
                {fieldItemWithActions}
            </Col>
        );
    };

    // renderAddRowField — field-level "addRow": repeats a SINGLE field's control
    // with the same add/remove + min/initial/max config the group-level addRow
    // uses (Form.List), but each list item IS the field's value directly (no
    // sub-key row object). Config keys mirror the group ones: field.minRows /
    // field.initialRows / field.maxRows / field.addLabel.
    //   • minRows    — delete is disabled once the list reaches this count.
    //   • initialRows— empty inputs when the record has no stored values.
    //   • maxRows    — the "Add" button hides once the list reaches this count.
    // Each stored value prefills its own item (buildRepeatFieldInitial reads the
    // field's stored array), and each item renders through singleFieldOf(field)
    // so a repeatable select shows a SINGLE control per item, not a multi-select.
    // Only used for a top-level field (namePrefix === undefined) — a field with
    // addRow=true inside an addRow GROUP renders as a normal single-value field
    // (no nested repeats).
    const renderAddRowField = (field) => {
        const fieldParts = typeof field.field === 'string' && field.field.includes('.')
            ? field.field.split('.')
            : [field.field];
        const permissionLocked = Boolean(field.editablePermission)
            && !roleAllowsAction(field.editablePermission, moduleName);
        const fieldLabel = <FieldLabel field={field} label={field.label} locked={permissionLocked} />;
        const itemField = singleFieldOf(field);
        // subFields → repeat a GROUP of fields per row (each row is an object of
        // the sub-fields, e.g. question + answer) instead of a single scalar.
        // Rows render through the same renderFields the group-level addRow uses.
        const hasSubFields = Array.isArray(field.subFields) && field.subFields.length > 0;
        // Sub-fields are always shown + editable (the admin exposes no show/edit
        // toggle for them). Force both so a sub-field never renders disabled: the
        // Go FormGroupField serializes show/edit as 0 when unset (no omitempty),
        // which editable() would otherwise read as "not editable".
        const subFieldsToRender = hasSubFields
            ? field.subFields.map((sf) => ({ ...sf, show: 1, edit: 1 }))
            : null;
        const minRows = Math.max(0, Number(field.minRows ?? 0) || 0);
        const maxRows = field.maxRows !== undefined && field.maxRows !== '' && Number(field.maxRows) > 0
            ? Number(field.maxRows) : 0;
        // field.value is the stored array (embedded by getFormGroups with an id).
        // The Form.List mounts once groups are populated, so this is correct at mount.
        const initialValue = buildRepeatFieldInitial(field, field.value);

        return (
            <Col key={field.field} xs={24} md={24}>
                <Form.Item label={fieldLabel} required={isFieldRequired(field)} colon={false} className="v1-combine-field">
                    <Form.List
                        name={fieldParts}
                        initialValue={initialValue}
                        rules={[{
                            validator: async (_, items = []) => {
                                if (isFieldRequired(field)) {
                                    const filled = hasSubFields
                                        ? items.some((v) => hasMeaningfulValue(v))
                                        : items.some((v) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0));
                                    if (!filled) throw new Error('Mandatory Field');
                                }
                                if (minRows > 0 && items.length < minRows) {
                                    throw new Error(`Keep at least ${minRows} ${hasSubFields ? 'row' : 'value'}${minRows > 1 ? 's' : ''}.`);
                                }
                            },
                        }]}
                    >
                        {(items, { add, remove }, { errors }) => (
                            <div className="v1-addrow-wrapper">
                                {items.map(({ key: itemKey, name: itemName }) => (
                                    <div key={itemKey} className="v1-addrow-item">
                                        <Button
                                            type="text" danger icon={<DeleteOutlined />}
                                            onClick={() => remove(itemName)}
                                            disabled={items.length <= minRows}
                                            className="v1-addrow-delete-btn"
                                        />
                                        <div className="v1-addrow-fields">
                                            {hasSubFields ? (
                                                <Row gutter={[24, 0]} align="top">
                                                    {renderFields(subFieldsToRender, itemName, fieldParts[0], false, field.field)}
                                                </Row>
                                            ) : (
                                                <Form.Item
                                                    name={[itemName]}
                                                    noStyle
                                                    valuePropName={field.type === 'checkbox' && !(field.options?.length) ? 'checked' : 'value'}
                                                    getValueFromEvent={field.type === 'file' ? (e) => (Array.isArray(e) ? e : e?.fileList) : undefined}
                                                    rules={getRules(itemField, form, [...fieldParts, itemName], true)}
                                                >
                                                    <FieldControl
                                                        field={itemField}
                                                        form={form}
                                                        name={[...fieldParts, itemName]}
                                                        moduleName={moduleName}
                                                        scopeValues={{ clientId }}
                                                        formDisabled={aiActions.busy}
                                                    />
                                                </Form.Item>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {(!maxRows || items.length < maxRows) && (
                                    <Button type="link" icon={<PlusOutlined />} onClick={() => add()} className="v1-addrow-add-btn">
                                        {field.addLabel ?? `Add ${field.label ?? 'Value'}`}
                                    </Button>
                                )}
                                <Form.ErrorList errors={errors} />
                            </div>
                        )}
                    </Form.List>
                </Form.Item>
            </Col>
        );
    };

    // renderNestedRepeatField — a repeat field (field.addRow) inside an addRow
    // group row. Nests its own Form.List under the current row so every row owns
    // independent repeat values (group[rowIndex].repeatField[repeatIndex]). Also
    // handles field.subFields (repeat a GROUP of sub-fields together per row,
    // e.g. question + answer) nested the same way — each item renders the
    // sub-fields through the same renderFields the top-level subFields branch
    // (renderAddRowField) uses, so a select/lookup sub-field gets identical
    // config-driven dropdown loading. namePrefix = the row index (from the
    // group Form.List); listName = the group's Form.List name (string), or a
    // doubly-nested list's absolute path array. Mirrors AddFormV1.
    const renderNestedRepeatField = (field, namePrefix, listName) => {
        const fieldParts = typeof field.field === 'string' && field.field.includes('.')
            ? field.field.split('.')
            : [field.field];
        const permissionLocked = Boolean(field.editablePermission)
            && !roleAllowsAction(field.editablePermission, moduleName);
        const fieldLabel = <FieldLabel field={field} label={field.label} locked={permissionLocked} />;
        const itemField = singleFieldOf(field);
        const hasSubFields = Array.isArray(field.subFields) && field.subFields.length > 0;
        const subFieldsToRender = hasSubFields
            ? field.subFields.map((sf) => ({ ...sf, show: 1, edit: 1 }))
            : null;
        const minRows = Math.max(0, Number(field.minRows ?? 0) || 0);
        const maxRows = field.maxRows !== undefined && field.maxRows !== '' && Number(field.maxRows) > 0
            ? Number(field.maxRows) : 0;
        const listNameRel = [namePrefix, ...fieldParts];
        const nestedAbsoluteListName = [...(Array.isArray(listName) ? listName : [listName]), namePrefix, ...fieldParts];
        // Empty starter inputs for a freshly-added row; an edited row inherits its
        // stored values from the parent row's initialValue (antd ignores this when
        // the store already holds a value at this path).
        const initialValue = buildRepeatFieldInitial(field, undefined);
        return (
            <Col key={`${listName}-${namePrefix}-${field.field}`} xs={24} md={hasSubFields ? 24 : (field.col ?? 24)}>
                <Form.Item label={fieldLabel} required={isFieldRequired(field)} colon={false} className="v1-combine-field">
                    <Form.List
                        name={listNameRel}
                        initialValue={initialValue}
                        rules={hasSubFields ? [{
                            validator: async (_, items = []) => {
                                if (isFieldRequired(field)) {
                                    const filled = items.some((v) => hasMeaningfulValue(v));
                                    if (!filled) throw new Error('Mandatory Field');
                                }
                                if (minRows > 0 && items.length < minRows) {
                                    throw new Error(`Keep at least ${minRows} row${minRows > 1 ? 's' : ''}.`);
                                }
                            },
                        }] : []}
                    >
                        {(items, { add, remove }, { errors }) => (
                            <div className="v1-addrow-wrapper">
                                {items.map(({ key: itemKey, name: itemName }) => {
                                    const absoluteName = [...nestedAbsoluteListName, itemName];
                                    return (
                                        <div key={itemKey} className="v1-addrow-item">
                                            <Button
                                                type="text" danger icon={<DeleteOutlined />}
                                                onClick={() => remove(itemName)}
                                                disabled={items.length <= minRows}
                                                className="v1-addrow-delete-btn"
                                            />
                                            <div className="v1-addrow-fields">
                                                {hasSubFields ? (
                                                    <Row gutter={[24, 0]} align="top">
                                                        {renderFields(subFieldsToRender, itemName, nestedAbsoluteListName, false, field.field)}
                                                    </Row>
                                                ) : (
                                                    <Form.Item
                                                        name={[itemName]}
                                                        noStyle
                                                        valuePropName={field.type === 'checkbox' && !(field.options?.length) ? 'checked' : 'value'}
                                                        getValueFromEvent={field.type === 'file' ? (e) => (Array.isArray(e) ? e : e?.fileList) : undefined}
                                                        rules={getRules(itemField, form, absoluteName, true)}
                                                    >
                                                        <FieldControl
                                                            field={itemField}
                                                            form={form}
                                                            name={absoluteName}
                                                            moduleName={moduleName}
                                                            scopeValues={{ clientId }}
                                                            formDisabled={aiActions.busy}
                                                        />
                                                    </Form.Item>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {(!maxRows || items.length < maxRows) && (
                                    <Button type="link" icon={<PlusOutlined />} onClick={() => add()} className="v1-addrow-add-btn">
                                        {field.addLabel ?? `Add ${field.label ?? 'Value'}`}
                                    </Button>
                                )}
                                <Form.ErrorList errors={errors} />
                            </div>
                        )}
                    </Form.List>
                </Form.Item>
            </Col>
        );
    };

    const renderFields = (fields, namePrefix, listName, hideLabel = false, groupNameForActions) => {
        const used = new Set();
        const out = [];
        fields.forEach((field) => {
            if (truthy(field.addRow) && namePrefix === undefined) {
                out.push(renderAddRowField(field));
                return;
            }
            // A repeat field INSIDE an addRow group row (namePrefix defined) nests
            // its own Form.List so each row keeps independent repeat values
            // (educationDetails[rowIndex].subjects[]) — including a subFields
            // repeat-GROUP (e.g. question+answer pairs per education row).
            if (truthy(field.addRow) && namePrefix !== undefined) {
                out.push(renderNestedRepeatField(field, namePrefix, listName));
                return;
            }
            const combineKey = field.combineKey?.trim?.();
            if (!combineKey) {
                out.push(renderField(field, namePrefix, listName, { hideLabel, groupNameForActions }));
                return;
            }
            const packKey = `${listName ?? 'root'}:${namePrefix ?? 'root'}:${combineKey}`;
            if (used.has(packKey)) return;
            const pack = fields.filter((f) => f.combineKey === combineKey);
            pack.forEach((f) => used.add(`${listName ?? 'root'}:${namePrefix ?? 'root'}:${f.combineKey}`));

            const label = pack.find((f) => f.combineLabel)?.combineLabel ?? field.combineLabel ?? field.label;
            const toggleTarget = pack.find((f) => f.combineToggleTarget)?.combineToggleTarget;
            const toggleLabel = pack.find((f) => f.combineToggleLabel)?.combineToggleLabel ?? 'Show range';
            const wrapPack = pack.some((f) => truthy(f.combineWrap));
            const configuredSpan = Number(pack.find((f) => [12, 24].includes(Number(f.combineSpan)))?.combineSpan);
            const targetField = toggleTarget ? pack.find((f) => fieldKey(f) === toggleTarget) : null;
            const targetParts = targetField?.field?.includes?.('.') ? targetField.field.split('.') : [targetField?.field].filter(Boolean);
            const targetName = targetField
                ? ((listName !== undefined && namePrefix !== undefined)
                    ? [listName, namePrefix, ...targetParts]
                    : (targetParts.length > 1 ? targetParts : targetField.field))
                : null;

            // One label, boxes inline on a single row, range toggle BELOW. Toggle off →
            // only the primary (target hidden); on → the target box appears alongside.
            const primaryReq = pack.some((f) => isFieldRequired(f));
            const comboSpan = [12, 24].includes(configuredSpan)
                ? configuredSpan
                : (pack.length >= 3 ? 24 : 12);
            out.push(
                <Col key={packKey} xs={24} md={comboSpan}>
                    <Form.Item label={label} required={primaryReq} colon={false} className="v1-combine-field">
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const hasTargetValue = targetName ? !empty(getFieldValue(targetName)) : false;
                                const checked = combineToggles[packKey] ?? hasTargetValue;
                                const visiblePack = toggleTarget && !checked
                                    ? pack.filter((f) => fieldKey(f) !== toggleTarget)
                                    : pack;
                                return (
                                    <>
                                        <Row
                                            gutter={[8, 0]}
                                            align="top"
                                            wrap={wrapPack}
                                            className={`v1-combine-row${wrapPack ? ' v1-combine-row-wrap' : ''}`}
                                        >
                                            {visiblePack.map((f) => renderField(f, namePrefix, listName, { inline: true }))}
                                        </Row>
                                        {toggleTarget && (
                                            <div className="v1-combine-range">
                                                <Switch
                                                    size="small"
                                                    checked={checked}
                                                    onChange={(next) => {
                                                        if (!next && targetName) {
                                                            form.setFieldValue(targetName, undefined);
                                                            form.validateFields([targetName]).catch(() => { });
                                                        }
                                                        setCombineToggles((prev) => ({ ...prev, [packKey]: next }));
                                                    }}
                                                />
                                                <Text type="secondary" className="v1-combine-toggle-label">{toggleLabel}</Text>
                                            </div>
                                        )}
                                    </>
                                );
                            }}
                        </Form.Item>
                    </Form.Item>
                </Col>,
            );
        });
        return out;
    };

    const renderGroup = (group, index) => {
        const key = group.name ?? group.id ?? String(index);
        const groupName = group.name ?? key;
        // group.linkGroup: when this group is linked to others, the LEADER owns
        // the Add button + minRows/requireOne validator for the whole set; every
        // member (leader + followers) still gets its own Form.List/Card/rows, and
        // add/remove always go through every member via listControlsRef so row
        // counts stay equal by construction. Unlinked groups (linkedSet === null,
        // i.e. every group that predates this feature) fall through leaderGroup
        // === group, so minRows/rules/initialValue below compute exactly as before.
        const linkedSet = findLinkedSet(linkedGroupSets, groupName);
        const isFollower = Boolean(linkedSet) && linkedSet.leaderName !== groupName;
        const leaderGroup = linkedSet
            ? (groups.find((g) => g.name === linkedSet.leaderName) ?? group)
            : group;
        const minRows = Math.max(0, Number(leaderGroup.minRows ?? 0) || 0);
        const linkedMemberNames = linkedSet ? linkedSet.memberNames : [groupName];
        const addLinkedRow = () => {
            linkedMemberNames.forEach((name) => listControlsRef.current[name]?.add());
        };
        const removeLinkedRow = (rowName) => {
            linkedMemberNames.forEach((name) => listControlsRef.current[name]?.remove(rowName));
        };

        const body = group.addRow ? (
            <Form.List
                name={groupName}
                initialValue={addRowInitials[group.name] ?? []}
                rules={isFollower ? [] : [
                    {
                        validator: async (_, rows = []) => {
                            // A group switched OFF via its own toggle (e.g. "Fresher" hiding
                            // Work Experience) is not rendered at all — don't force rows on it.
                            const switchedOn = truthy(group.toggleEnabled) && group.toggleField
                                && Boolean(form.getFieldValue(group.toggleField));
                            if (switchedOn) return;
                            if (minRows > 0 && rows.length < minRows) {
                                throw new Error(`Keep at least ${minRows} row${minRows > 1 ? 's' : ''}.`);
                            }
                            if (truthy(group.requireOne)) {
                                const invalid = rows.some((row) => !hasMeaningfulValue(row));
                                if (invalid) throw new Error('Fill at least one field in each added row.');
                            }
                        },
                    },
                ]}
            >
                {(rows, { add, remove }, { errors }) => {
                    listControlsRef.current[groupName] = { add, remove };
                    return (
                        <div className="v1-addrow-wrapper">
                            <div className="v1-addrow-topbar">
                                {!isFollower ? (
                                    <Button
                                        type="link"
                                        icon={<PlusOutlined />}
                                        onClick={addLinkedRow}
                                        className="v1-addrow-add-btn"
                                    >
                                        {group.addLabel ?? `Add ${group.label ?? 'Row'}`}
                                    </Button>
                                ) : (
                                    <Text type="secondary" className="v1-addrow-linked-hint">
                                        Rows added via {leaderGroup.label ?? leaderGroup.name}
                                    </Text>
                                )}
                            </div>

                            {truthy(group.sharedRowLabels) && rows.length > 0 && (
                                <Row gutter={[24, 0]} className="v1-addrow-header">
                                    {group.fields.map((field) => (
                                        <Col key={fieldKey(field)} md={field.col ?? 12} className="v1-addrow-header-label">
                                            {field.label}
                                        </Col>
                                    ))}
                                </Row>
                            )}

                            {rows.map(({ key: rowKey, name }) => (
                                <div key={rowKey} className="v1-addrow-item">
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        disabled={rows.length <= minRows}
                                        onClick={() => removeLinkedRow(name)}
                                        className="v1-addrow-delete-btn"
                                    />
                                    <div className="v1-addrow-fields">
                                        <Row gutter={[24, 0]} align="top">
                                            {renderFields(group.fields, name, groupName, truthy(group.sharedRowLabels), groupName)}
                                        </Row>
                                    </div>
                                </div>
                            ))}
                            <Form.ErrorList errors={errors} />
                        </div>
                    );
                }}
            </Form.List>
        ) : (
            <Row gutter={[24, 0]}>{renderFields(group.fields, undefined, undefined, false, groupName)}</Row>
        );

        return (
            <Card
                key={key}
                className="v1-card v1-edit-card"
                title={
                    <SectionTitle
                        icon={sectionIcons[index % sectionIcons.length]}
                        title={group.label ?? group.name ?? 'Details'}
                    />
                }
                extra={
                    <Space size={8} align="center" className="v1-group-actions">
                        <GroupInfoMessage group={group} />
                        <GroupToggleControl group={group} />
                        <Button type="text" size="small"
                            icon={collapsed[key] ? <DownOutlined /> : <UpOutlined />}
                            onClick={(e) => { e.stopPropagation(); toggle(key); }}
                        />
                    </Space>
                }
            >
                <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                        const switchedOn = truthy(group.toggleEnabled) && group.toggleField && Boolean(getFieldValue(group.toggleField));
                        return <div style={{ display: collapsed[key] || switchedOn ? 'none' : 'block' }}>{body}</div>;
                    }}
                </Form.Item>
            </Card>
        );
    };

    return (
        <main>
            {resolvedBreadcrumbs.length > 0 && <AppBreadcrumb items={resolvedBreadcrumbs} />}

            <div className="v1-edit-page">
                <div className="v1-page-inner">
                    <Spin spinning={loading || aiActions.busy} tip={aiActions.busy ? 'Running AI action…' : 'Loading form fields...'}>
                        {error && (
                            <Alert type="error" showIcon message={error}
                                style={{ marginBottom: 16 }} />
                        )}
                        {submitError && (
                            <Alert type="error" showIcon message={submitError}
                                style={{ marginBottom: 16 }} />
                        )}

                        <Form
                            form={form}
                            layout="vertical"
                            colon={false}
                            className="v1-form"
                            disabled={aiActions.busy}
                            requiredMark={(label, { required }) => (
                                <>
                                    {label}
                                    {required && <span className="v1-required-mark"> *</span>}
                                </>
                            )}
                            onFinish={submit}
                            onFinishFailed={handleSubmitFailed}
                        >
                            {groups.map(renderGroup)}

                            {!loading && !error && groups.length === 0 && (
                                <Alert type="info" showIcon message="No form fields are configured." />
                            )}
                        </Form>
                    </Spin>
                </div>

                <div className="v1-footer">
                    <Text type="secondary">
                        All unsaved changes will be lost if you cancel and reload.
                    </Text>
                    <Space size={12}>
                        <Button onClick={() => (cancelPath ? navigate(cancelPath) : navigate(-1))}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={() => form.submit()}
                            disabled={loading || submitting} loading={submitting}>
                            {submitText}
                        </Button>
                    </Space>
                </div>
            </div>
        </main>
    );
}
